export const LOCAL_IFC_ARTIFACT = {
  packageName: "ifc-bim-json",
  version: "0.1.90",
  sourceCommit: "683f0e8bc2e95afb5ca269e943ce0512c3fa34e0",
  archiveName: "ifc-bim-json-web-0.1.90-683f0e8.tar.gz",
  archiveBytes: 500800,
  archiveDigest: "c9243a0026efacbda203af6ddd64311dd9ce92e8fc8116b3b85a8be2d24b04b6",
  wasmDigest: "90345b6e0b2df885b53831091e8d87740356d057c755291e7fef34f65fb1444b",
  importOptions: "{}"
} as const;

export interface StagedFailure {
  readonly contractVersion: "ifc-staged-input/1";
  readonly ok: false;
  readonly error: { readonly code: string; readonly message: string };
}

export interface StagedSuccess<T> {
  readonly contractVersion: "ifc-staged-input/1";
  readonly ok: true;
  readonly value: T;
}

export type StagedResult<T> = StagedFailure | StagedSuccess<T>;

export interface LocalIfcImporter {
  open(filename: string, totalBytes: number, optionsJson: string): StagedResult<{ readonly handle: number }>;
  push(handle: number, offset: number, bytes: Uint8Array): StagedResult<unknown>;
  finish(handle: number): StagedResult<{ readonly responseJson: string }>;
  close(handle: number): StagedResult<unknown>;
  stats(): StagedResult<unknown>;
  importOneShot(bytes: Uint8Array, filename: string, optionsJson: string): string;
  memoryBytes?(): number | null;
}

export interface LocalIfcSource {
  readonly filename: string;
  readonly bytes: Uint8Array;
}

export interface LocalIfcSourceIdentity {
  readonly filename: string;
  readonly byteLength: number;
  readonly sha256: string;
}

export interface LocalIfcObservation {
  readonly state: "observed" | "refused";
  readonly reason?: string;
  readonly source: LocalIfcSourceIdentity;
  readonly importer: typeof LOCAL_IFC_ARTIFACT;
  readonly raw: unknown;
  readonly unknownTopLevel: Readonly<Record<string, unknown>>;
  readonly outcomeCensus: { readonly observed: number; readonly refused: number };
  readonly stagedSession?: { readonly close: unknown; readonly statsAfterClose: unknown };
}

const stagedChunkBytes = 256 * 1024;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const ownedBytes = new Uint8Array(bytes);
  const digest = await crypto.subtle.digest("SHA-256", ownedBytes.buffer);
  return [...new Uint8Array(digest)].map((part) => part.toString(16).padStart(2, "0")).join("");
}

export function normalizeImporterRaw(raw: unknown): {
  readonly state: "observed" | "refused";
  readonly reason?: string;
  readonly unknownTopLevel: Readonly<Record<string, unknown>>;
} {
  if (!isRecord(raw)) {
    return { state: "refused", reason: "Importer returned a non-object response.", unknownTopLevel: {} };
  }

  const { plan, report, error, ...unknownTopLevel } = raw;
  if (typeof error === "string") {
    return { state: "refused", reason: error, unknownTopLevel };
  }
  if (plan === null) {
    return {
      state: "refused",
      reason: isRecord(report) && typeof report.error === "string" ? report.error : "Importer refused the local IFC source.",
      unknownTopLevel
    };
  }
  if (plan === undefined) {
    return { state: "refused", reason: "Importer response omitted its observation plan.", unknownTopLevel };
  }
  return { state: "observed", unknownTopLevel };
}

function observationFromRaw(source: LocalIfcSourceIdentity, raw: unknown): LocalIfcObservation {
  const normalized = normalizeImporterRaw(raw);
  return {
    ...normalized,
    source,
    importer: LOCAL_IFC_ARTIFACT,
    raw,
    outcomeCensus: normalized.state === "observed" ? { observed: 1, refused: 0 } : { observed: 0, refused: 1 }
  };
}

function refusalFromStagedFailure(source: LocalIfcSourceIdentity, failure: StagedFailure): LocalIfcObservation {
  return observationFromRaw(source, { error: failure.error.message, report: { stagedInputError: failure.error } });
}

export async function observeLocalIfc(importer: LocalIfcImporter, source: LocalIfcSource): Promise<LocalIfcObservation> {
  const identity: LocalIfcSourceIdentity = {
    filename: source.filename,
    byteLength: source.bytes.byteLength,
    sha256: await sha256Hex(source.bytes)
  };

  if (source.bytes.byteLength === 0) {
    return observationFromRaw(identity, JSON.parse(importer.importOneShot(source.bytes, source.filename, LOCAL_IFC_ARTIFACT.importOptions)));
  }

  const opened = importer.open(source.filename, source.bytes.byteLength, LOCAL_IFC_ARTIFACT.importOptions);
  if (!opened.ok) {
    return refusalFromStagedFailure(identity, opened);
  }

  let observation: LocalIfcObservation | undefined;
  let closeResult: unknown;
  let statsAfterClose: unknown;
  try {
    for (let offset = 0; offset < source.bytes.byteLength; offset += stagedChunkBytes) {
      const pushed = importer.push(opened.value.handle, offset, source.bytes.slice(offset, offset + stagedChunkBytes));
      if (!pushed.ok) {
        observation = refusalFromStagedFailure(identity, pushed);
        break;
      }
    }
    if (observation === undefined) {
      const finished = importer.finish(opened.value.handle);
      observation = finished.ok
        ? observationFromRaw(identity, JSON.parse(finished.value.responseJson))
        : refusalFromStagedFailure(identity, finished);
    }
  } catch (error) {
    observation = observationFromRaw(identity, { error: error instanceof Error ? error.message : "Local IFC observation failed." });
  } finally {
    closeResult = importer.close(opened.value.handle);
    statsAfterClose = importer.stats();
  }
  if (observation === undefined) throw new Error("Local IFC observation did not produce an outcome.");
  return { ...observation, stagedSession: { close: closeResult, statsAfterClose } };
}
